import React from "react";
import Seo from "@/lib/Seo";
import { CONTACT } from "@/data/content";

export default function Privacy() {
  return (
    <div>
      <Seo
        path="/privacy"
        title="Privacy Policy"
        description="How Steady Path Funding collects, uses, and shares information from visitors and applicants — including data sharing with lender partners, cookies, and your rights."
      />

      <section className="pt-40 pb-16 border-b border-black/10">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <div className="overline">Legal · Effective July 2026</div>
          <h1 className="mt-6 font-serif text-6xl md:text-7xl leading-[0.95]" data-testid="privacy-heading">
            Privacy <em className="italic text-[color:var(--gold)]">Policy</em>.
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <div className="prose-editorial" data-testid="privacy-body">
            <p>
              Steady Path Funding ("<strong>Steady Path</strong>," "<strong>we</strong>," "<strong>us</strong>," or
              "<strong>our</strong>") respects your privacy. This Privacy Policy
              explains how we collect, use, and share information when you visit
              our website, apply for financing through us, subscribe to our
              newsletter, or otherwise interact with us.
            </p>
            <p>
              We are a <strong>commercial finance brokerage</strong>, not a lender.
              We help small businesses find and compare capital across a network
              of third-party lenders. That relationship shapes how your
              information moves — please read this policy carefully.
            </p>

            <h2>1. Information we collect</h2>
            <p>We collect the following categories of information:</p>
            <ul>
              <li>
                <strong>Contact and business information you submit</strong> — your
                name, business name, business type, email address, mobile and
                business phone numbers, state of operation, time in business,
                monthly revenue range, credit score range, funding amount
                requested, product preference, and any notes you share on our
                lead or application forms.
              </li>
              <li>
                <strong>SMS opt-in details</strong> — if you elect to receive text
                message updates after applying, we store the phone number,
                consent timestamp, and consent status.
              </li>
              <li>
                <strong>Newsletter subscription details</strong> — email address
                and referring source (e.g., blog).
              </li>
              <li>
                <strong>Automatically collected information</strong> — IP address,
                browser type, device type, referring URL, pages visited, and
                time on page. This is collected via Google Analytics 4 (GA4) and
                similar first-party analytics tools.
              </li>
              <li>
                <strong>Cookies and similar technologies</strong> — small text files
                stored on your device that help us understand traffic and
                improve the site. You can disable cookies in your browser
                settings; some features may not work as intended.
              </li>
            </ul>

            <h2>2. How we use your information</h2>
            <ul>
              <li>To match your business file with lender partners best suited to your funding request.</li>
              <li>To contact you by phone, email, or (if opted in) SMS regarding your inquiry or application.</li>
              <li>To send transactional confirmations, reference codes, and status updates.</li>
              <li>To send educational content and newsletter updates you subscribed to (opt-out any time).</li>
              <li>To operate, secure, and improve the site and our services.</li>
              <li>To comply with legal, regulatory, and record-keeping obligations.</li>
            </ul>

            <h2>3. Sharing with lender partners and service providers</h2>
            <p>
              To place your file, we share the information you submit with one
              or more third-party lenders and lending platforms in our network.
              These lenders are independent companies with their own privacy
              practices. Once your information reaches a lender, that lender's
              privacy policy also applies to that lender's use of the
              information. We do not sell your personal information to
              third-party marketers.
            </p>
            <p>We also use trusted service providers to run the business, including:</p>
            <ul>
              <li>Email delivery (transactional and newsletter)</li>
              <li>SMS/text delivery (only if you opt in)</li>
              <li>Analytics (Google Analytics 4)</li>
              <li>Hosting, database, and infrastructure providers</li>
            </ul>
            <p>
              These providers process information on our behalf under contract
              and are not permitted to use it for their own marketing purposes.
            </p>

            <h2>4. Your SMS and email consent</h2>
            <p>
              <strong>SMS.</strong> Opting in to broker text updates is optional
              and never required to receive funding. Message and data rates may
              apply. Message frequency varies. Reply <strong>STOP</strong> to any
              text to opt out; reply <strong>HELP</strong> for assistance. We
              honor STOP requests immediately.
            </p>
            <p>
              <strong>Email.</strong> Transactional emails (application
              confirmations, reference codes, autoresponders) are sent as part of
              the service you requested. Marketing/newsletter emails include an
              unsubscribe link; you can also email {" "}
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>{" "}
              to be removed.
            </p>

            <h2>5. Credit inquiries</h2>
            <p>
              Submitting a lead form or application to us does <strong>not</strong>
              {" "}result in a hard credit pull. If, after reviewing offers, you
              formally choose to move forward with a lender, that lender may
              conduct a hard credit inquiry as part of underwriting — you will
              be told before that happens.
            </p>

            <h2>6. Data retention</h2>
            <p>
              We retain submission and application records for the period needed
              to serve you, respond to follow-up inquiries, and satisfy legal or
              regulatory obligations. Analytics data is retained per Google
              Analytics defaults (subject to change). You may request deletion of
              your personal information by emailing us at{" "}
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>. We may
              retain limited records where required by law.
            </p>

            <h2>7. Your rights</h2>
            <p>
              Depending on where you live, you may have rights to access,
              correct, delete, or restrict the use of your personal information
              (for example, under the California Consumer Privacy Act, as
              amended by the CPRA, and similar state laws). To exercise these
              rights, contact us at{" "}
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>. We will
              respond within the timeframes required by law and will not
              discriminate against you for exercising your rights.
            </p>

            <h2>8. Security</h2>
            <p>
              We use administrative and technical safeguards designed to protect
              information we collect. No internet transmission or electronic
              storage is 100% secure, however, so we cannot guarantee absolute
              security. Please do not send sensitive information (like your
              full Social Security number, full bank account number, or a
              password) through unsecured channels.
            </p>

            <h2>9. Children's privacy</h2>
            <p>
              The site and services are intended for business owners and are
              not directed to children under 16. We do not knowingly collect
              information from children.
            </p>

            <h2>10. Third-party links</h2>
            <p>
              Our site may link to third-party sites, including lenders and
              educational resources. We are not responsible for the privacy
              practices of those sites.
            </p>

            <h2>11. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do,
              we will revise the "Effective" date at the top. Material changes
              will be highlighted on the site.
            </p>

            <h2>12. Contact us</h2>
            <p>
              Questions about this Privacy Policy or your information? Email us
              at <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> or call{" "}
              <a href={`tel:${CONTACT.mainPhone.replace(/[^0-9]/g, "")}`}>
                {CONTACT.mainPhone}
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
